/**
 * OfficialIcons.jsx
 * Detailed line-icon SVGs for the portal value section:
 *   • PassportIcon     — closed Saudi-style passport that opens on hover
 *   • StampIcon        — three overlapping official round stamps
 *   • SchengenVisaIcon — rectangular Schengen-visa sticker with EU stars + MRZ
 *
 * All icons are pure outline (no fills), gold-tinted via the `color` prop,
 * and sized via the `size` prop.
 */

/* ============================== Passport ============================== */
/**
 * Closed cover → flips open on hover. Cover shows the Saudi national emblem
 * (crossed swords + palm tree silhouette), an Arabic title bar, and a small
 * shield. Inside spread reveals a stamp and personal-data lines.
 */
export function PassportIcon({ open, color, size = 40 }) {
  return (
    <div style={{ width: size, height: size, position: "relative", perspective: "750px" }}>
      {/* Inside spread (revealed when the cover swings open) */}
      <svg viewBox="0 0 40 40" width={size} height={size} fill="none"
        style={{ position: "absolute", inset: 0 }}>
        <rect x="3" y="6" width="34" height="28" rx="2.5" stroke={color} strokeWidth="1.2" />
        {/* Spine */}
        <line x1="20" y1="6" x2="20" y2="34" stroke={color} strokeWidth="0.8" strokeDasharray="1.6 1.4" />
        {/* Left page: round stamp */}
        <circle cx="11.5" cy="17" r="3.2" stroke={color} strokeWidth="1" strokeDasharray="1.4 1" />
        <circle cx="11.5" cy="17" r="1.6" stroke={color} strokeWidth="0.8" />
        <line x1="7" y1="24" x2="15.5" y2="24" stroke={color} strokeWidth="0.9" />
        <line x1="7" y1="26.5" x2="14" y2="26.5" stroke={color} strokeWidth="0.7" />
        <line x1="7" y1="29" x2="13" y2="29" stroke={color} strokeWidth="0.7" />
        {/* Right page: personal data lines + photo box */}
        <rect x="23" y="11" width="6" height="7.5" rx="0.5" stroke={color} strokeWidth="0.9" />
        <line x1="30.5" y1="12" x2="36" y2="12" stroke={color} strokeWidth="0.8" />
        <line x1="30.5" y1="14.5" x2="35" y2="14.5" stroke={color} strokeWidth="0.7" />
        <line x1="30.5" y1="17" x2="36" y2="17" stroke={color} strokeWidth="0.7" />
        <line x1="23" y1="22" x2="36" y2="22" stroke={color} strokeWidth="0.8" />
        <line x1="23" y1="25" x2="35" y2="25" stroke={color} strokeWidth="0.7" />
        <line x1="23" y1="28" x2="36" y2="28" stroke={color} strokeWidth="0.7" />
      </svg>

      {/* Closed cover — flips from right edge (spine) */}
      <svg viewBox="0 0 40 40" width={size} height={size} fill="none"
        style={{
          position: "absolute", inset: 0,
          transformOrigin: "right center",
          transform: open ? "rotateY(-145deg)" : "rotateY(0deg)",
          transition: "transform 650ms cubic-bezier(0.4, 0, 0.2, 1)",
          backfaceVisibility: "hidden",
        }}>
        {/* Outer cover */}
        <rect x="8" y="3" width="24" height="34" rx="2.5" stroke={color} strokeWidth="1.5"
          fill="rgba(212,175,55,0.05)" />
        {/* Crown / palm-tree emblem (Saudi style: palm + crossed swords) */}
        <g transform="translate(20 12)">
          {/* palm trunk */}
          <line x1="0" y1="2" x2="0" y2="-3" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
          {/* palm fronds */}
          <path d="M0 -3 q-3 -1 -4 -3 M0 -3 q3 -1 4 -3 M0 -3 q-2 -2 -2 -4 M0 -3 q2 -2 2 -4"
            stroke={color} strokeWidth="0.9" fill="none" strokeLinecap="round" />
          {/* crossed swords */}
          <line x1="-5" y1="4.5" x2="5" y2="-0.5" stroke={color} strokeWidth="0.9" strokeLinecap="round" />
          <line x1="5" y1="4.5" x2="-5" y2="-0.5" stroke={color} strokeWidth="0.9" strokeLinecap="round" />
        </g>
        {/* "PASSPORT" title bar */}
        <line x1="12" y1="22" x2="28" y2="22" stroke={color} strokeWidth="1.2" />
        <line x1="14" y1="25" x2="26" y2="25" stroke={color} strokeWidth="0.8" />
        {/* Bottom country shield */}
        <path d="M14 29 L26 29 L26 32 Q26 34 20 35 Q14 34 14 32 Z"
          stroke={color} strokeWidth="1" fill="none" />
      </svg>
    </div>
  );
}

/* ============================== Stamps ============================== */
/**
 * Three overlapping official round/triangular stamps. The top stamp has a
 * dashed border ring (typical of immigration stamps), the second has a star
 * center, and the third is a triangular date stamp.
 */
export function StampIcon({ color, size = 40 }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} fill="none">
      {/* Back stamp — triangle (date stamp) */}
      <g transform="rotate(-15 14 25)">
        <path d="M7 19 L21 19 L14 30 Z" stroke={color} strokeWidth="1.2" />
        <line x1="9.5" y1="23" x2="18.5" y2="23" stroke={color} strokeWidth="0.8" />
        <line x1="11" y1="25.5" x2="17" y2="25.5" stroke={color} strokeWidth="0.7" />
      </g>
      {/* Middle stamp — square approved */}
      <g transform="rotate(8 27 23)">
        <rect x="20" y="16" width="14" height="14" stroke={color} strokeWidth="1.2" />
        <line x1="22.5" y1="20" x2="31.5" y2="20" stroke={color} strokeWidth="0.9" />
        <line x1="22.5" y1="23" x2="29.5" y2="23" stroke={color} strokeWidth="0.7" />
        <line x1="22.5" y1="26" x2="30.5" y2="26" stroke={color} strokeWidth="0.7" />
      </g>
      {/* Front stamp — round official (dashed outer ring + star) */}
      <g transform="rotate(-6 18 15)">
        <circle cx="18" cy="15" r="10" stroke={color} strokeWidth="1.4" strokeDasharray="1.8 1.4" />
        <circle cx="18" cy="15" r="6.5" stroke={color} strokeWidth="1" />
        {/* Star inside */}
        <path d="M18 11 L18.9 13.5 L21.5 13.5 L19.3 15 L20.2 17.5 L18 16 L15.8 17.5 L16.7 15 L14.5 13.5 L17.1 13.5 Z"
          stroke={color} strokeWidth="0.9" fill="none" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

/* ============================ Schengen Visa ============================ */
/**
 * Official Schengen-visa sticker. Horizontal rectangle with:
 *   • "SCHENGEN" top-left bar
 *   • EU stars circle top-right
 *   • photo box on the left
 *   • personal-data field lines on the right
 *   • MRZ (machine readable zone) two-line block at the bottom
 */
export function SchengenVisaIcon({ color, size = 40 }) {
  // 12 EU stars positioned in a circle
  const stars = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * Math.PI * 2) / 12 - Math.PI / 2;
    const cx = 32 + Math.cos(angle) * 4.2;
    const cy = 11 + Math.sin(angle) * 4.2;
    return <circle key={i} cx={cx} cy={cy} r="0.65" fill={color} stroke="none" />;
  });

  return (
    <svg viewBox="0 0 40 40" width={size} height={size} fill="none">
      {/* Sticker outer frame */}
      <rect x="3" y="6" width="34" height="28" rx="2.5"
        stroke={color} strokeWidth="1.4" fill="rgba(212,175,55,0.04)" />
      {/* "SCHENGEN" header bar */}
      <rect x="5.5" y="8.5" width="15" height="3" rx="0.6" stroke={color} strokeWidth="0.9" />
      {/* EU stars circle (top-right) */}
      <circle cx="32" cy="11" r="5.2" stroke={color} strokeWidth="0.8" strokeDasharray="0.5 0.5" />
      {stars}
      {/* Photo placeholder (left side) */}
      <rect x="5.5" y="14" width="7.5" height="9" rx="0.5" stroke={color} strokeWidth="1" />
      <circle cx="9.25" cy="17.5" r="1.4" stroke={color} strokeWidth="0.8" />
      <path d="M6.5 22.5 Q9.25 19.5 12 22.5" stroke={color} strokeWidth="0.7" />
      {/* Personal data lines (right of photo) */}
      <line x1="14.5" y1="14.5" x2="34" y2="14.5" stroke={color} strokeWidth="0.85" />
      <line x1="14.5" y1="17" x2="32" y2="17" stroke={color} strokeWidth="0.7" />
      <line x1="14.5" y1="19.5" x2="34" y2="19.5" stroke={color} strokeWidth="0.7" />
      <line x1="14.5" y1="22" x2="30" y2="22" stroke={color} strokeWidth="0.7" />
      {/* MRZ (machine readable zone) — two rows of "<<" represented as
          alternating dashes for that authentic visa look */}
      <g stroke={color} strokeWidth="0.6">
        {Array.from({ length: 14 }, (_, i) => (
          <line key={`m1-${i}`} x1={5.5 + i * 2.2} y1="27" x2={6.8 + i * 2.2} y2="27" />
        ))}
        {Array.from({ length: 14 }, (_, i) => (
          <line key={`m2-${i}`} x1={5.5 + i * 2.2} y1="30" x2={6.8 + i * 2.2} y2="30" />
        ))}
      </g>
    </svg>
  );
}
